import { useEffect, useState } from 'react';
import { superadminApi } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { Activity, Building2, Anchor, TrendingUp, MessageSquare, DollarSign, UserPlus } from 'lucide-react';

interface OverviewData {
  orgs: {
    total: number;
    by_status: Record<string, number>;
    by_tier: Record<string, number>;
    new_last_30_days: number;
  };
  marinas: { total: number };
  this_month: {
    chats: number;
    messages: number;
    claude_cost_eur: number;
    bookings: number;
    booking_revenue_eur: number;
  };
}

function StatCard({
  label,
  value,
  sub,
  icon: Icon,
  color = 'text-brand-600',
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ComponentType<{ className?: string }>;
  color?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
      <div className={`p-2.5 rounded-lg bg-gray-50 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export function SuperadminOverviewPage() {
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    superadminApi.getOverview()
      .then((d) => setData(d as unknown as OverviewData))
      .catch(() => toast.error('Failed to load overview'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-400">Loading...</div>
    );
  }

  if (!data) return null;

  const tierBadge: Record<string, string> = {
    free: 'bg-slate-100 text-slate-700',
    starter: 'bg-gray-100 text-gray-700',
    growth: 'bg-blue-100 text-blue-700',
    group: 'bg-purple-100 text-purple-700',
    enterprise: 'bg-amber-100 text-amber-700',
  };

  const statusBadge: Record<string, string> = {
    trialing: 'bg-sky-100 text-sky-700',
    active: 'bg-green-100 text-green-700',
    past_due: 'bg-orange-100 text-orange-700',
    expired: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-500',
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Platform Overview</h1>
        <p className="text-sm text-gray-500 mt-1">Real-time snapshot of all marinas and organizations.</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Building2} label="Total Organizations" value={data.orgs.total} sub={`+${data.orgs.new_last_30_days} last 30 days`} />
        <StatCard icon={Anchor} label="Active Marinas" value={data.marinas.total} color="text-teal-600" />
        <StatCard icon={MessageSquare} label="Chats This Month" value={data.this_month.chats.toLocaleString()} sub={`${data.this_month.messages.toLocaleString()} messages`} color="text-indigo-600" />
        <StatCard icon={Activity} label="Bookings This Month" value={data.this_month.bookings.toLocaleString()} sub={`€${data.this_month.booking_revenue_eur.toFixed(2)} revenue`} color="text-emerald-600" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={DollarSign} label="Claude Cost (Month)" value={`€${data.this_month.claude_cost_eur.toFixed(2)}`} color="text-rose-600" />
        <StatCard icon={TrendingUp} label="New Sign-ups (30d)" value={data.orgs.new_last_30_days} color="text-violet-600" />
        <StatCard
          icon={UserPlus}
          label="Paying Subscribers"
          value={(data.orgs.by_tier['starter'] ?? 0) + (data.orgs.by_tier['growth'] ?? 0) + (data.orgs.by_tier['group'] ?? 0) + (data.orgs.by_tier['enterprise'] ?? 0)}
          sub={`${data.orgs.by_status['active'] ?? 0} active (incl. free) · ${data.orgs.by_status['trialing'] ?? 0} trialing`}
          color="text-amber-600"
        />
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By tier */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Organizations by Tier</h2>
          <div className="space-y-2">
            {Object.entries(data.orgs.by_tier).map(([tier, count]) => (
              <div key={tier} className="flex items-center justify-between">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${tierBadge[tier] ?? 'bg-gray-100 text-gray-600'}`}>{tier}</span>
                <span className="text-sm font-semibold text-gray-800">{count}</span>
              </div>
            ))}
            {Object.keys(data.orgs.by_tier).length === 0 && (
              <p className="text-sm text-gray-400">No data</p>
            )}
          </div>
        </div>

        {/* By status */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Organizations by Status</h2>
          <div className="space-y-2">
            {Object.entries(data.orgs.by_status).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${statusBadge[status] ?? 'bg-gray-100 text-gray-600'}`}>{status.replace(/_/g, ' ')}</span>
                <span className="text-sm font-semibold text-gray-800">{count}</span>
              </div>
            ))}
            {Object.keys(data.orgs.by_status).length === 0 && (
              <p className="text-sm text-gray-400">No data</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
